import React from 'react';
import { withTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  Form,
  FormInstance,
  InputGroup,
  Schema,
  toaster,
} from 'rsuite';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { constant, util } from '@helper';
import _ from 'lodash';
import { SignUpPayload } from '@common/ApiTypes';
import constants from '@helper/constants';
import { AuthAPI } from '@services/api';

import linkedinLogo from '@assets/logo-linkedin-white.png';
import googleLogo from '@assets/logo-google.png';

const { withRouter } = util;
const { StringType } = Schema.Types;

// The page paints its own full-bleed background. `m-auto` on the content
// wrapper keeps it centered while still collapsing to the padding when the
// form is taller than the viewport, so it never gets clipped at the top.
const SCREEN_CLASS = [
  'flex min-h-screen flex-col px-4',
  'pt-[max(6rem,12.5vh)] pb-[max(1.5rem,3.5vh)]',
  'font-semibold leading-[1.4] text-[#212121]',
].join(' ');

// The background is a fixed viewport layer (not a class on the scroll
// container) so it still covers the visible area when the document is
// taller than this screen. It must NOT use a negative z-index:
// `.app`/`body` have white backgrounds, and a negative z-index layer would
// paint underneath them. Instead the panel is given its own stacking
// context (relative z-0) so it paints above the layer, which comes first
// in DOM order.
const BACKGROUND_CLASS =
  'fixed inset-0 bg-candidate-login bg-cover bg-no-repeat';

const PANEL_CLASS = 'relative z-0 m-auto w-full max-w-xl px-5';

type State = {
  signup: SignUpPayload;

  showPassword: boolean;
  showConfirmPassword: boolean;
  agreementChecked: boolean;
};

class CandidateSignUpScreen extends React.Component<any, State> {
  formSignUpRef: React.Ref<
    FormInstance<
      Record<string, any>,
      string,
      {
        [x: string]: string | undefined;
      }
    >
  >;

  formSignUpSchemaModel: any;

  constructor(props: any) {
    super(props);

    this.state = {
      signup: {
        email: '',
        password: '',
        confirmPassword: '',
        name: '',
      },
      showConfirmPassword: false,
      showPassword: false,
      agreementChecked: false,
    };

    this.formSignUpRef = React.createRef();
    this.formSignUpSchemaModel = Schema.Model({
      name: StringType().isRequired('Please fill the required field'),
      email: StringType()
        .isEmail('Invalid email address format')
        .isRequired('Please fill the required field'),
      password: StringType().isRequired('Please fill the required field'),
      confirmPassword: StringType()
        .addRule((value, data) => {
          if (!_.isEqual(value, data.password)) {
            return false;
          }

          return true;
        }, 'The confirm password is not matched.')
        .isRequired('Please fill the required field'),
    });
  }

  private onFormValueChanged = (value: object, key: string): void =>
    this.setState((prevState) => ({
      ...prevState,
      [key]: value,
    }));

  private onSignUpButtonClicked = async (): Promise<void> => {
    const { t, navigate } = this.props;

    if (!_.invoke(this.formSignUpRef, 'current.check')) {
      toaster.push(
        util.buildErrorMessage(
          t(constant.translation.signup.formErrorValidationMessageKey),
        ),
      );
      return;
    }

    try {
      const { signup: payload } = this.state;
      await AuthAPI.signUp(payload);

      // Show pop-up message
      toaster.push(
        util.buildMessage(
          t(constant.translation.signup.formAfterSignUpMessageKey),
        ),
      );

      navigate('/email-verification', {
        state: { email: payload.email },
      });
    } catch (e) {
      // Throw pop-up error
      const response = _.get(e, 'response.data');
      const errors: string | Array<any> | any =
        _.get(response, 'errors') || _.get(response, 'message');
      let message;

      if (_.isArray(errors)) {
        message = errors.join('\n');
      } else {
        message = errors;
      }

      toaster.push(util.buildErrorMessage(message));

      // Erase the password
      this.setState((prevState) => ({
        ...prevState,
        signup: {
          ...prevState.signup,
          password: '',
          confirmPassword: '',
        },
      }));
    }
  };

  private onGoogleSignUpButtonClicked = (): void => {
    try {
      window.open(
        `${process.env.REACT_APP_BASE_URL}/api/v1/auth/google`,
        '_self',
      );
    } catch (e: any) {
      // Throw pop-up error
      const response = _.get(e, 'response.data');
      const errors: string | Array<any> | any =
        _.get(response, 'errors') || _.get(response, 'message');
      let message;

      if (_.isArray(errors)) {
        message = errors.join('\n');
      } else {
        message = errors;
      }

      toaster.push(util.buildErrorMessage(message));
    }
  };

  private onLinkedInSignInButtonClicked = (): void => {
    try {
      window.open(
        `${process.env.REACT_APP_BASE_URL}/api/v1/auth/linkedin`,
        '_self',
      );
    } catch (e: any) {
      // Throw pop-up error
      const response = _.get(e, 'response.data');
      const errors: string | Array<any> | any =
        _.get(response, 'errors') || _.get(response, 'message');
      let message;

      if (_.isArray(errors)) {
        message = errors.join('\n');
      } else {
        message = errors;
      }

      toaster.push(util.buildErrorMessage(message));
    }
  };

  render({ t } = this.props): JSX.Element | null {
    const { showPassword, showConfirmPassword, agreementChecked } = this.state;

    return (
      <div className={SCREEN_CLASS}>
        <div aria-hidden className={BACKGROUND_CLASS} />

        <div className={PANEL_CLASS}>
          <header className="px-5 pb-5">
            <p
              // eslint-disable-next-line max-len
              className="mb-4 !leading-[1.2] text-center text-[32px] font-bold whitespace-pre-line"
            >
              {t(constant.translation.signup.pageCandidateTitleKey)}
            </p>
            <small
              // eslint-disable-next-line max-len
              className="block text-center text-base font-normal text-[#595959] opacity-75"
            >
              {t(constant.translation.signup.pageCandidateSubTitleKey)}
            </small>
          </header>

          <Form
            key="signup-form"
            ref={this.formSignUpRef}
            model={this.formSignUpSchemaModel}
            onChange={(v: object) => this.onFormValueChanged(v, 'signup')}
            formValue={_.get(this.state, 'signup')}
            fluid
          >
            <Form.Group className="mt-10">
              <Form.ControlLabel>
                {t(constants.translation.general.formFullNameKey)}
              </Form.ControlLabel>
              <Form.Control name="name" placeholder="Enter your fullname" />
            </Form.Group>

            <Form.Group>
              <Form.ControlLabel>
                {t(constants.translation.general.formEmailKey)}
              </Form.ControlLabel>
              <Form.Control
                name="email"
                placeholder="Enter your email address"
              />
            </Form.Group>

            <Form.Group>
              <Form.ControlLabel>
                {t(constants.translation.general.formPasswordKey)}
              </Form.ControlLabel>
              <InputGroup inside>
                <Form.Control
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password here"
                />

                <InputGroup.Button
                  onClick={() => this.setState({ showPassword: !showPassword })}
                >
                  {showPassword ? <FaEye /> : <FaEyeSlash />}
                </InputGroup.Button>
              </InputGroup>
            </Form.Group>

            <Form.Group>
              <Form.ControlLabel>
                {t(constants.translation.general.formConfirmPasswordKey)}
              </Form.ControlLabel>
              <InputGroup inside>
                <Form.Control
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Enter your confirm password here"
                />

                <InputGroup.Button
                  onClick={() =>
                    this.setState({ showConfirmPassword: !showConfirmPassword })
                  }
                >
                  {showConfirmPassword ? <FaEye /> : <FaEyeSlash />}
                </InputGroup.Button>
              </InputGroup>
            </Form.Group>

            <div
              // eslint-disable-next-line max-len
              className="mb-8 flex items-center gap-2 font-normal"
            >
              <Checkbox
                onChange={() =>
                  this.setState({ agreementChecked: !agreementChecked })
                }
              />
              By creating an account, I agree to our{' '}
              <a
                target="_blank"
                href="/terms-condition"
                // eslint-disable-next-line max-len
                className="cursor-pointer font-bold text-[#212121] hover:!text-[#212121]"
              >
                Terms & Conditions
              </a>
            </div>

            <Button
              className="btn-tg-primary"
              block
              appearance="primary"
              disabled={!agreementChecked}
              onClick={this.onSignUpButtonClicked}
            >
              {t(constant.translation.general.formSignUpButtonKey)}
            </Button>

            <p className="mt-5 text-center font-normal">
              Already have an account?&nbsp;
              <a
                href="/login"
                // eslint-disable-next-line max-len
                className="cursor-pointer font-bold text-[#004AAD] hover:!text-[#004AAD]"
              >
                Log In
              </a>
            </p>

            <p className="my-5 text-center font-normal">or</p>

            <Button
              className="btn-tg-google"
              block
              appearance="primary"
              onClick={this.onGoogleSignUpButtonClicked}
            >
              <img alt="logo" src={googleLogo} />
              <div>
                {t(
                  constant.translation.login.pageCandidateContinueWithLabelKey,
                )}
                <b>Google</b>
              </div>
            </Button>

            <Button
              className="btn-tg-linkedin !mt-5"
              block
              appearance="primary"
              onClick={this.onLinkedInSignInButtonClicked}
            >
              <img alt="logo" src={linkedinLogo} />
              <div>
                {t(
                  constant.translation.login.pageCandidateContinueWithLabelKey,
                )}
                <b>LinkedIn</b>
              </div>
            </Button>
          </Form>
        </div>
      </div>
    );
  }
}

export default withRouter(withTranslation()(CandidateSignUpScreen));
